import { Actions, Async, F1 } from '@client/new/helpers/types';
import _ from 'lodash';
import {
  useGetLivestreamStatusQuery,
  useSetLivestreamingMutation,
} from 'generated/graphql';
import toast from 'react-hot-toast';
import { useState } from 'react';
import { getLivestreamingEnabled } from '@client/new/common/features.helpers';

type LivestreamViewModelParams = {
  influencerId: number;
};

export type LivestreamViewModel = {
  dispatch: LivestreamViewDispatcher;
  isLivestreamEnabled: boolean;
  submitting: boolean;
  influencerId: number;
};

export type LivestreamViewActionsMap = {
  submitEnable: null;
};

export type LivestreamViewActions = Actions<LivestreamViewActionsMap>;

export type LivestreamViewDispatcher = F1<LivestreamViewActions>;

export const useLivestreamViewModel = (
  p: LivestreamViewModelParams,
): Async<LivestreamViewModel> => {
  const [submitting, setSubmitting] = useState(false);
  const [setLivestreamingMutation] = useSetLivestreamingMutation();

  const { data, loading, error, refetch } = useGetLivestreamStatusQuery({
    fetchPolicy: 'network-only',
    variables: { id: p.influencerId },
    skip: !p.influencerId,
  });

  if (loading) {
    return {
      type: 'fetching',
    };
  }

  if (error) {
    return {
      type: 'fetch-error',
      value: 'Could not fetch data',
    };
  }

  const onEnableLivestream = async () => {
    setSubmitting(true);
    try {
      await setLivestreamingMutation({
        variables: {
          data: {
            influencerId: p.influencerId,
            isEnabled: true,
          },
        },
      });
      await refetch();
      toast.success('Livestreaming is now available!');
    } catch (e) {
      toast.error('Something went wrong!');
    } finally {
      setSubmitting(false);
    }
  };

  const dispatch: LivestreamViewDispatcher = (action) => {
    switch (action.type) {
      case 'submitEnable':
        onEnableLivestream();
        return null;
    }
  };

  const isLivestreamEnabled = getLivestreamingEnabled(data?.creator?.features);

  return {
    type: 'fetched',
    value: {
      dispatch,
      isLivestreamEnabled,
      submitting,
      influencerId: p.influencerId,
    },
  };
};
